import { useMemo } from 'react';
import { Popper } from '@material-ui/core';
import { MDSButton, MDSDropdown } from '@marqvision/mds-v2';
import { useGetAllTrademarkedLogosQuery } from '~/apis/monitoring/filter';
import { useGetFilterParams, usePathParams } from '~/components/Filters/@hooks';
import { TrademarkedLogoImagePreview, useTrademarkPreview } from '~/components/Filters/@hooks/useTrademarkPreview';
import { ResponsiveProps } from '~/components/Responsive/@types';
import { useLogger } from '~/hooks';
import { getListingTabGroup, parseDropDown } from '~/utils';

export const TrademarkLogoFilter = (props: ResponsiveProps) => {
  const { size = 'medium' } = props;
  const { params, requestFilterParams, setParams, removeParams } = useGetFilterParams();
  const { page, listingStatus } = usePathParams();
  const { amplitudeLogging } = useLogger();

  const trademarkedLogosQuery = useGetAllTrademarkedLogosQuery(requestFilterParams, {
    select: parseDropDown<number>,
  });

  const list = useMemo(() => {
    return trademarkedLogosQuery?.data?.list || [];
  }, [trademarkedLogosQuery.data]);

  const hasTrademarkData = list.length > 0;
  const trademarkPreview = useTrademarkPreview(list);

  const selectedValues = useMemo(() => {
    if (!params.tm_logo_ids) return [];
    const tmLogoIds = Array.isArray(params.tm_logo_ids) ? params.tm_logo_ids : [params.tm_logo_ids];
    return tmLogoIds.includes(-1) ? list.map((item) => item.value) : tmLogoIds;
  }, [params.tm_logo_ids, list]);

  const handleSelect = (value: (string | number)[]) => {
    if (value.length === 0) {
      removeParams(['tm_logo', 'tm_logo_ids']);
    } else {
      const isSelectAll = value.length === list.length;
      amplitudeLogging('applied filter', {
        Type: 'tm_logo_ids',
        Channel: page,
        Page: getListingTabGroup(listingStatus),
      });

      if (isSelectAll) {
        setParams({ tm_logo: true, tm_logo_ids: [-1] }, { replace: true });
      } else {
        removeParams(['tm_logo']);
        setParams({ tm_logo_ids: value as number[] }, { replace: true });
      }
    }
  };

  const handleAllClick = () => {
    if (params.tm_logo_ids) {
      removeParams(['tm_logo', 'tm_logo_ids']);
    } else {
      setParams({ tm_logo: true, tm_logo_ids: [-1] }, { replace: true });
    }
  };

  const variant = params.tm_logo_ids ? 'fill' : 'tint';

  return (
    <div>
      <MDSButton
        variant={variant}
        color="bluegray"
        onClick={handleAllClick}
        isDisabled={list.length === 0}
        flat="right"
        isLoading={trademarkedLogosQuery.isLoading}
      >
        Trademarked logo used
      </MDSButton>
      <MDSDropdown
        width={400}
        isLoading={trademarkedLogosQuery.isLoading}
        modules={[
          'hide-select-all',
          {
            type: 'filter-button',
            flat: 'left',
            size,
          },
        ]}
        list={list}
        value={selectedValues}
        onChange={handleSelect}
        onOpen={hasTrademarkData ? trademarkPreview.onOpen : undefined}
        onClose={hasTrademarkData ? trademarkPreview.onClose : undefined}
      />
      {hasTrademarkData && (
        <Popper
          anchorEl={trademarkPreview.anchorEl}
          open={!!(trademarkPreview.anchorEl && trademarkPreview.previewItem)}
          placement="left-start"
        >
          {trademarkPreview.previewItem && (
            <TrademarkedLogoImagePreview
              url={trademarkPreview.previewItem.imgUrl || ''}
              bBoxList={trademarkPreview.previewItem.bbox_list}
            />
          )}
        </Popper>
      )}
    </div>
  );
};
